import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import { booleanToString } from '../../../../admin/utils';
import { getCampaignDebugUrl, isAdminContext } from '../../../../utils/adminUrlUtils';
import * as AdminModels from '../../../../models/AdminModels';
import {
	useBulkEmailCampaignHistoricalStatsQuery,
	useEmailQuery,
	useEmailUserCapacityQuery,
	useUserQuery,
	useUserStatsQuery,
} from '../../../../queries';
import { StackedBarChart } from '../../../components/charts/StackedBarChart';
import { ContactsTableActiveSearchesList } from '../../../components/contacts/ContactsTableActiveSearchesList';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { MultiContainerHeader } from '../../../components/MultiContainerHeader';
import { ComputerCoffeeGraphic } from '../../../components/svgs/graphics/ComputerCoffeeGraphic';
import { XIcon } from '../../../components/svgs/icons/XIcon';
import { Button } from '../../../components-v2/Button';
import { charmGray, destructive, success } from '../../../styles/colors';
import { baseStyleSheet } from '../../../styles/styles';
import { styleSheet } from './styles';

function getChartData(blocks: AdminModels.IBulkEmailHistoricalTimeBlock[]) {
	return blocks.map<AdminModels.IBulkEmailHistoryDataPoint>(x => {
		return {
			ErrorMessages: x.errorMessages,
			Failures: x.failures,
			Hour: moment(x.block).local().format('M-D-YY @ hA'),
			Successes: x.successes,
		};
	});
}

function getSearches(campaign: Api.ICampaign) {
	return campaign.filterRequest?.criteria
		? Api.VmUtils.sortContactFilterCriteria(campaign.filterRequest.criteria).searches.filter(x =>
				Api.VmUtils.isTagSearchContactFilterCriteria(x)
			)
		: [];
}

export function EmailCampaignHistoricalReport() {
	const { accountId, campaignId } = useParams<{ accountId: string; campaignId: string }>();
	const [selectedTimeBlock, setSelectedTimeBlock] = React.useState<string[]>();
	const [prevHistoricalReport, setPrevHistoricalReport] = React.useState<AdminModels.IBulkEmailHistoricalReport>();
	const [showFullCriteria, setShowFullCriteria] = React.useState(false);

	// Debug button logic
	const adminContext = isAdminContext();

	const onDebugCampaign = React.useCallback(() => {
		if (campaignId) {
			// Use the impersonated account ID if available, otherwise regular debug URL
			const debugUrl = getCampaignDebugUrl(campaignId, accountId || undefined);
			window.open(debugUrl, '_blank');
		}
	}, [campaignId, accountId]);

	const accImpCtx = React.useMemo(() => {
		return {
			account: {
				id: accountId,
			},
		} as Api.IImpersonationContext;
	}, [accountId]);
	const campaignQuery = useEmailQuery({ id: campaignId, impersonationContext: accImpCtx });
	const bulkEmailReportQuery = useBulkEmailCampaignHistoricalStatsQuery({
		campaignId,

		userId: campaignQuery?.data?.creator?.id,
		enabled: !!campaignQuery?.data?.creator?.id,
	});

	// eslint-disable-next-line eqeqeq
	if (prevHistoricalReport != bulkEmailReportQuery.data) {
		if (bulkEmailReportQuery.data != null) {
			// probably a superfluous check
			const firstTimeBlock = getChartData(bulkEmailReportQuery.data.blocks ?? [])?.[0];
			if (firstTimeBlock?.ErrorMessages) {
				setSelectedTimeBlock(firstTimeBlock?.ErrorMessages);
			}
		}
		setPrevHistoricalReport(bulkEmailReportQuery.data);
	}
	const userImpCtx = React.useMemo(() => {
		if (!campaignQuery.data?.creator?.id) {
			return null;
		}
		return { account: { id: accountId }, user: { id: campaignQuery.data.creator.id } } as Api.IImpersonationContext;
	}, [accountId, campaignQuery.data]);
	const userStatsQuery = useUserStatsQuery({ enabled: !!userImpCtx, impersonationContext: userImpCtx });
	const creatorQuery = useUserQuery({
		enabled: Boolean(campaignQuery.data?.creator?.id),
		impersonationContext: userImpCtx,
		id: campaignQuery.data?.creator?.id,
	});
	const emailCapacityQuery = useEmailUserCapacityQuery({
		impersonationContext: userImpCtx,
		enabled: !!userImpCtx,
	});

	const toggleFullCriteria = () => {
		setShowFullCriteria(!showFullCriteria);
	};
	const selectHour = (data: AdminModels.IBulkEmailHistoryDataPoint) => {
		setSelectedTimeBlock(data.ErrorMessages);
	};

	const getUsedCapacity = (intervalInMinutes: number) => {
		if (!emailCapacityQuery.data?.currentActivity?.recentActivity) {
			return 0;
		}
		const activity = emailCapacityQuery.data.currentActivity.recentActivity.find(
			stat => stat.intervalInMinutes === intervalInMinutes
		);
		return activity?.count ?? 0;
	};

	const getAvailableCapacity = (intervalInMinutes: number) => {
		if (!emailCapacityQuery.data?.mailerTypeCapacities) {
			return 'N/A';
		}

		const currentMailer = emailCapacityQuery.data.mailerTypeCapacities.find(
			mailer => mailer.mailerType === campaignQuery.data.mailerType
		);
		if (!currentMailer) {
			return 'N/A';
		}

		const limit = currentMailer.sendLimits.find(l => l.intervalInMinutes === intervalInMinutes);
		if (!limit) {
			return '-';
		}

		const used = getUsedCapacity(intervalInMinutes);
		const available = limit.count - used;
		return Math.max(0, available);
	};

	const renderAdditionalInfo = () => {
		return (
			<>
				<div className={css(styleSheet.limitsContainer)}>
					<div className={css(styleSheet.limitSection)}>
						<h3 className={css(styleSheet.sectionTitle)}>Limits</h3>
						<div className={css(styleSheet.limitGroup)}>
							<span className={css(styleSheet.limitTitle)}>Daily</span>
							<span className={css(styleSheet.limit)}>{bulkEmailReportQuery.data.limits?.dailyLimit ?? 'None'}</span>
						</div>
						<div className={css(styleSheet.limitGroup)}>
							<span className={css(styleSheet.limitTitle)}>Hourly</span>
							<span className={css(styleSheet.limit)}>{bulkEmailReportQuery.data.limits?.hourlyLimit ?? 'None'}</span>
						</div>
					</div>

					<div className={css(styleSheet.usageSection)}>
						<h3 className={css(styleSheet.sectionTitle)}>Remaining Capacity</h3>
						<div className={css(styleSheet.limitGroup)}>
							<span className={css(styleSheet.limitTitle)}>Daily</span>
							<span className={css(styleSheet.limit)}>
								{emailCapacityQuery.isPending ? 'Loading...' : getAvailableCapacity(1440)}
							</span>
						</div>
						<div className={css(styleSheet.limitGroup)}>
							<span className={css(styleSheet.limitTitle)}>Hourly</span>
							<span className={css(styleSheet.limit)}>
								{emailCapacityQuery.isPending ? 'Loading...' : getAvailableCapacity(60)}
							</span>
						</div>
					</div>
				</div>
				<div className={css(baseStyleSheet.horizontalStack, styleSheet.additionalInfoContainer)}>
					<div className={css(styleSheet.limitTitle, styleSheet.additionalInfoItem)}>
						last comm error date
						<div className={css(styleSheet.limit)}>
							{!campaignQuery.data ? 'Loading...' : ((campaignQuery.data?.lastCommErrorDate as string) ?? 'none')}
						</div>
					</div>
					<div className={css(styleSheet.limitTitle, styleSheet.additionalInfoItem)}>
						start date
						<div className={css(styleSheet.limit)}>
							{!campaignQuery.data
								? 'Loading...'
								: campaignQuery.data?.schedule?.startDate
									? moment(campaignQuery.data.schedule.startDate).format('MM/DD/YY @ h:mm a')
									: '--'}
						</div>
					</div>
					<div className={css(styleSheet.limitTitle, styleSheet.additionalInfoItem)}>
						sender connected to provider
						<div className={css(styleSheet.limit)}>
							{!userStatsQuery.data ? 'Loading...' : booleanToString(userStatsQuery.data.emailCalendarTokenExists)}
						</div>
					</div>
					<div className={css(styleSheet.limitTitle, styleSheet.additionalInfoItem)}>
						Contact Resolved
						<div className={css(styleSheet.limit)}>
							{!campaignQuery.data
								? 'Loading...'
								: campaignQuery.data.hasResolvedTransactions
									? `Resolved at ${
											campaignQuery.data.startResolutionDate
												? moment(new Date(campaignQuery.data.startResolutionDate)).format('h:mm a')
												: '--'
										}`
									: 'Not Resolved'}
						</div>
					</div>
					<div className={css(styleSheet.limitTitle, styleSheet.additionalInfoItem)}>
						Email Created
						<div className={css(styleSheet.limit)}>
							{!campaignQuery.data
								? 'Loading...'
								: campaignQuery.data.creationDate
									? moment(new Date(campaignQuery.data.creationDate)).format('MM/DD/YY @ h:mm a')
									: '--'}
						</div>
					</div>

					<div className={css(styleSheet.limitTitle, styleSheet.additionalInfoItem)}>
						Sender timezone
						<div className={css(styleSheet.limit)}>
							{!creatorQuery?.data ? 'Loading...' : (creatorQuery?.data?.userPreferences?.timeZone ?? 'none')}
						</div>
					</div>
				</div>

				{campaignQuery.data.categories?.length ? (
					<div className={css(styleSheet.additionalInfoContainer)}>
						<div className={css(styleSheet.limitTitle, styleSheet.additionalInfoRecipients)}>Categories</div>
						<div className={css(baseStyleSheet.horizontalStack)}>
							{campaignQuery.data.categories.map(cat => (
								<div key={cat} className={css(styleSheet.category)}>
									{cat}
								</div>
							))}
						</div>
					</div>
				) : null}

				{campaignQuery.data.contactsFilterRequest ? (
					<div className={css(styleSheet.additionalInfoContainer)}>
						<div className={css(styleSheet.limitTitle, styleSheet.additionalInfoRecipients)}>
							Recipients{' '}
							<span className={css(baseStyleSheet.brandLink, styleSheet.showFullCriteria)} onClick={toggleFullCriteria}>
								(Show full criteria)
							</span>
						</div>
						{campaignQuery.data.contactsFilterRequest.ownershipFilter && (
							<div className={css(styleSheet.limitGroup)}>
								<span className={css(styleSheet.limitTitle)}>Ownership Filter:</span>
								<span className={css(styleSheet.limit)}>
									{campaignQuery.data.contactsFilterRequest.ownershipFilter.title || 'Applied'}
								</span>
							</div>
						)}
						<ContactsTableActiveSearchesList
							filterCriteria={campaignQuery.data.filterRequest}
							hideAdvancedSearch={true}
							hideLabel={true}
							hideRemoveButtons={true}
							searches={getSearches(campaignQuery.data)}
							tokenPrefixStyles={[styleSheet.tagTokenPrefix]}
						/>
						{showFullCriteria && (
							<div className={css(styleSheet.additionalInfoContainer, styleSheet.fullCriteriaContainer)}>
								<span className={css(baseStyleSheet.link, styleSheet.closeFullCriteria)} onClick={toggleFullCriteria}>
									<XIcon color={charmGray} height={12} width={12} />
								</span>
								<textarea
									className={css(styleSheet.fullCriteria)}
									disabled={true}
									value={JSON.stringify(campaignQuery.data.filterRequest, null, 4)}
								/>
							</div>
						)}
					</div>
				) : null}
			</>
		);
	};
	const renderNoAttempts = () => {
		return (
			<div className={css(styleSheet.empty)}>
				<div className={css(baseStyleSheet.flexCenter)}>
					<ComputerCoffeeGraphic />
				</div>
				<div className={css(styleSheet.noneFound)}>No attempts found for this bulk email</div>
			</div>
		);
	};
	const renderReportContent = () => {
		const chart = {
			barStack: ['Successes', 'Failures'],

			data: getChartData(bulkEmailReportQuery.data.blocks ?? []),
			fillColors: { Failures: destructive, Successes: success },
			xAxis: 'Hour',
		};
		return (
			<>
				{renderAdditionalInfo()}

				{bulkEmailReportQuery.data.blocks?.length > 0 ? (
					<div>
						<StackedBarChart chart={chart} height={500} onBarClick={selectHour} />
						<div className={css(styleSheet.errorContainer)}>
							<div>Error Messages</div>

							{selectedTimeBlock?.length > 0 && selectedTimeBlock[0] !== null ? (
								<div className={css(styleSheet.errors)}>
									{selectedTimeBlock.map(x => {
										return (
											<div key={x} className={css(styleSheet.error)}>
												{x}
											</div>
										);
									})}
								</div>
							) : (
								<div className={css(styleSheet.noErrors)}>No errors for selected hour</div>
							)}
						</div>
					</div>
				) : (
					renderNoAttempts()
				)}
			</>
		);
	};

	return (
		<div className={css(styleSheet.container)}>
			<MultiContainerHeader fullscreenHeader='Group Email Report' />
			<div className={css(styleSheet.content)}>
				<div className={css(styleSheet.innerContainer)}>
					<div className={css(styleSheet.titleContainer)}>
						<div className={css(styleSheet.title)}>
							{campaignQuery.data ? campaignQuery.data.subject : <LoadingSpinner type='tiny' />}
						</div>
						{adminContext && campaignId && (
							<Button variant='secondary' size='sm' onClick={onDebugCampaign} styles={[styleSheet.debugButton]}>
								Debug Campaign
							</Button>
						)}
					</div>
					{bulkEmailReportQuery.isPending ? <LoadingSpinner type='large' /> : renderReportContent()}
				</div>
			</div>
		</div>
	);
}
